<template>
  <div>
    <div class="login-content-body-content">
      <form v-if="!showVtspAccounts && !isPinRequired" class="vstack gap-3" data-testid="login-form">
        <email-input v-if="showEmail" v-model="email" data-testid="login-username" />

        <div v-if="showPassword" class="d-flex flex-column">
          <div class="vstack gap-3">
            <div v-if="this.formState === 'PASSWORD'" class="fw-bold me-2" style="font-size: 1rem">
              {{ email.value }}
              <a class="link-primary text-decoration-none fw-normal" href="#" @click="changeEmail">{{
                $t('flexigo.login.change')
              }}</a>
            </div>
            <password-input v-model="password" :name="'admin-password'" data-testid="login-password" />
            <div v-if="loginErrorMsg" class="text-danger">
              {{ loginErrorMsg }}
            </div>
          </div>

          <button type="button" class="link-dark mt-2 align-self-end" @click="handleForgotPassword">
            {{ $t('flexigo.login.forgotMyPassword') }}
          </button>
          <p class="invalid-feedback">
            {{ $t('flexigo.login.password') }}
          </p>
        </div>

        <Recaptcha
          v-if="isCaptchaRequired"
          class="mt-3 align-self-center"
          @verified="onRecaptchaVerified"
          @expired="onRecaptchaExpired"
        />

        <div class="login-submit-content my-3">
          <large-button
            :type="'submit'"
            :disabled="!isValid"
            :is-loading="isLoading"
            data-testid="login-submit"
            @click="() => handleLogin()"
          >
            {{ formState === 'EMAIL' ? $t('flexigo.login.continue') : $t('flexigo.login.login') }}
          </large-button>
        </div>

        <div v-if="!isAdmin" class="fs-5">
          {{ $t('flexigo.login.dontHaveAnAccount') }}
          <router-link :to="{ name: 'register' }" class="fw-bold ms-2 link-black">
            {{ $t('flexigo.login.signUp') }}
          </router-link>
        </div>
      </form>

      <div v-if="isPinRequired" class="vstack gap-3 mb-5">
        <div class="fw-bold" style="font-size: 1rem">
          {{ $t('flexigo.login.pinVerification.title') }}
        </div>
        <p class="m-0">{{ $t('flexigo.login.pinVerification.description') }}</p>
        <div class="vstack gap-3">
          <input
            v-model="pinCode"
            type="text"
            inputmode="numeric"
            pattern="[0-9]*"
            class="form-control"
            :placeholder="$t('flexigo.login.pinVerification.placeholder')"
            autocomplete="one-time-code"
            @input="validatePinInput"
          />
          <div v-if="pinErrorMsg" class="text-danger">
            {{ pinErrorMsg }}
          </div>
        </div>
        <div class="login-submit-content my-3">
          <large-button :disabled="!isPinValid" :is-loading="isLoading" @click="submitPin">
            {{ $t('flexigo.login.login') }}
          </large-button>
        </div>
      </div>

      <select-account v-if="isVTSPAccountSelectorVisible" :options="accountsList" @on-select="onSelectAccount" />
    </div>
  </div>
</template>

<script>
import { isLocaleEnUs, isEmpty } from '@/helpers/utility';
import registration from '../../services/registration';
import { writeEnvironments, writeCurrentEnvironment, clearEnvironments } from '../../services/environmentSwitcher';
import toaster from '../../mixins/toaster';
import loginRedirect from './loginRedirect';
import emailInput from './emailInput.vue';
import passwordInput from './passwordInput.vue';
import selectAccount from './selectAccount.vue';
import largeButton from './largeButton.vue';
import Recaptcha from '../../../../common/components/Recaptcha.vue';

export default {
  name: 'formLogin',
  components: { emailInput, passwordInput, selectAccount, largeButton, Recaptcha },
  mixins: [toaster, loginRedirect],
  props: {
    selectedUserType: {
      type: String,
      required: false,
    },
    existingEmail: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      email: { value: null, isValid: false },
      password: null,
      checkValid: false,
      loginErrorMsg: '',
      showVtspAccounts: false,
      accountsList: [],
      isLoading: false,
      isCaptchaRequired: false,
      captchaId: null,
      formState: 'BOTH',
      isPinRequired: false,
      pinCode: '',
      pinErrorMsg: '',
    };
  },
  computed: {
    isAdmin() {
      return this.selectedUserType === 'ADMIN';
    },
    isValid() {
      if (this.isLoading) {
        return false;
      }
      if (this.isCaptchaRequired && !this.captchaId) {
        return false;
      }
      if (this.formState === 'BOTH') {
        return this.email?.isValid && this.password?.length;
      }
      if (this.formState === 'EMAIL') {
        return this.email?.isValid;
      }
      return this.password?.length > 0;
    },
    showEmail() {
      return ['BOTH', 'EMAIL'].includes(this.formState);
    },
    showPassword() {
      return ['BOTH', 'PASSWORD'].includes(this.formState);
    },
    formValues() {
      return `${this.email.value}${this.password}`;
    },
    isPinValid() {
      return this.pinCode && this.pinCode.length === 5 && /^[0-9]+$/.test(this.pinCode);
    },
    isVTSPAccountSelectorVisible() {
      return this.showVtspAccounts && this.accountsList.length > 0 && !this.isPinRequired;
    },
  },
  watch: {
    existingEmail: {
      handler(newVal) {
        this.getRedirectedEmail(newVal);
      },
      deep: true,
    },
    selectedUserType() {
      this.adjustFormState();
    },
    formValues() {
      if (this.loginErrorMsg && this.password?.length) {
        this.loginErrorMsg = '';
      }
    },
    showVtspAccounts(newVal) {
      this.$emit('show-vtsp-accounts', newVal);
    },
  },
  mounted() {
    this.adjustFormState();
    this.getRedirectedEmail(this.existingEmail);
  },
  methods: {
    /**
     * Load the session company's settings for every account type (see the
     * Flexigo shell for the rationale, VSER-14481 #6d). A FLEXIGO_VEKTOR_ADMIN
     * with no company in session gets a 403 — that must not block the login
     * redirect.
     */
    async loadCompanySettings() {
      try {
        await this.$store.dispatch('SET_COMPANY_SETTINGS');
      } catch (error) {
        console.warn('Company settings unavailable for this session', error?.response?.status || error);
      }
    },
    handleForgotPassword() {
      this.$emit('open-screen', { screen: 'FORGOT_PASSWORD', email: this.email });
    },
    handleLogin() {
      this.checkValid = true;

      if (this.formState === 'EMAIL') {
        this.verifyEmail();
        return;
      }
      this.login();
    },
    continueWithAccountSelect(loginResponse) {
      this.showVtspAccounts = true;
      this.accountsList = loginResponse.vtspAccounts;
      // Cache the environment list so the in-app switcher can offer it later.
      writeEnvironments(loginResponse.vtspAccounts);
    },
    continueWithPassword() {
      this.formState = 'PASSWORD';
    },
    continueWithSso(loginResponse) {
      if (loginResponse.ssoUrl) {
        window.location.assign(loginResponse.ssoUrl);
      }
    },
    async verifyEmail() {
      try {
        const payload = { email: this.email?.value, langCode: isLocaleEnUs() ? 'en' : 'tr' };
        const loginResponse = await registration.checkAndLoginPersonnel(payload);
        if (loginResponse.status === 'CONTINUE_WITH_PASSWORD') {
          this.continueWithPassword();
        } else {
          this.continueWithSso(loginResponse);
        }
      } catch (error) {
        this.getToaster('error', error?.response?.data?.error?.message);
      }
    },

    async login(telematicAccount = null) {
      this.checkValid = true;
      this.isLoading = true;
      // Fresh attempt (not the account re-pick): drop any prior user's cached
      // environments so only this login's response repopulates them — prevents a
      // previous admin's client list leaking to the next user on a shared browser.
      if (!telematicAccount) clearEnvironments();
      try {
        const payload = {
          email: this.email.value,
          password: this.password,
          captchaId: this.captchaId || null,
          sicilNo: '',
        };
        if (telematicAccount) {
          payload.telematicAccount = telematicAccount;
        }
        const loginResponse = await registration[this.isAdmin ? 'login' : 'loginPersonnel'](payload);

        if (loginResponse.pinRequired) {
          this.isPinRequired = true;
          this.getToaster('success', this.$t('flexigo.login.pinVerification.smsSent'));
          return;
        }

        if (!isEmpty(loginResponse.vtspAccounts) && !telematicAccount) {
          this.continueWithAccountSelect(loginResponse);
        } else {
          // Remember which environment we bound to (the picked account), so the
          // switcher can highlight it. Null for single-environment admins.
          if (telematicAccount) writeCurrentEnvironment(telematicAccount);
          this.getToaster('success', this.$t('flexigo.login.redirecting'), this.$t('flexigo.login.loginSuccessful'));

          await this.$store.dispatch('SET_USER_DETAILS');
          await this.loadCompanySettings();
          this.loginRedirect(this.$store.state.userDetails);
        }
        this.loginErrorMsg = '';
      } catch (error) {
        this.password = '';
        this.isCaptchaRequired = error?.response?.data?.error?.requiresCaptcha;
        if (error?.response?.data?.error?.errorId === 192) {
          this.loginErrorMsg = this.$t('flexigo.login.wrongUser.message');
        } else {
          this.getToaster('error', error?.response?.data?.error?.message);
        }
      } finally {
        this.isLoading = false;
      }
    },
    onSelectAccount(selectedAccount) {
      this.login(selectedAccount);
    },
    validatePinInput(event) {
      const { value } = event.target;
      const numericValue = value.replace(/[^0-9]/g, '');

      if (numericValue.length > 5) {
        this.pinCode = numericValue.slice(0, 5);
      } else {
        this.pinCode = numericValue;
      }
    },
    async submitPin() {
      this.isLoading = true;
      this.pinErrorMsg = '';
      try {
        await registration.verifyPin({ pinCode: this.pinCode });
        this.getToaster('success', this.$t('flexigo.login.redirecting'), this.$t('flexigo.login.loginSuccessful'));
        await this.$store.dispatch('SET_USER_DETAILS');
        await this.loadCompanySettings();
        this.loginRedirect(this.$store.state.userDetails);
      } catch (error) {
        if (!error.response) {
          this.pinErrorMsg =
            this.$t('flexigo.common.networkError') || 'Network error. Please check your connection and try again.';
        } else {
          this.pinErrorMsg = this.$t('flexigo.login.pinVerification.invalidPin');
        }
        this.pinCode = '';
      } finally {
        this.isLoading = false;
      }
    },
    onRecaptchaVerified(token) {
      this.captchaId = encodeURIComponent(token);
    },
    onRecaptchaExpired() {
      this.captchaId = null;
    },
    adjustFormState() {
      if (this.isAdmin) {
        this.formState = 'BOTH';
      } else {
        this.formState = 'EMAIL';
      }
    },
    changeEmail() {
      this.formState = 'EMAIL';
      this.resetForm();
    },
    getRedirectedEmail(email) {
      if (!email || isEmpty(email)) {
        return;
      }
      this.email = { ...email };
      if (!this.isAdmin) {
        this.formState = 'PASSWORD';
      }
    },
    resetForm() {
      this.email = { value: null, isValid: false };
      this.password = null;
    },
  },
};
</script>
